import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface SavedAddress {
  id: string;
  user_id: string;
  label: string;
  address: string;
  zone_id: string | null;
  place_type: string | null;
  is_default: boolean | null;
  created_at: string;
  updated_at: string;
}

export function useSavedAddresses() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['saved-addresses', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('saved_addresses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SavedAddress[];
    },
    enabled: !!user,
  });
}

export function useAddAddress() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ label, address, zone_id, place_type }: { label: string; address: string; zone_id?: string; place_type?: string }) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('saved_addresses')
        .insert({ 
          user_id: user.id, 
          label, 
          address,
          zone_id: zone_id || null,
          place_type: place_type || null
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-addresses'] });
    },
  });
}

export function useUpdateAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, label, address, zone_id, place_type }: { id: string; label: string; address: string; zone_id?: string | null; place_type?: string | null }) => {
      const { error } = await supabase
        .from('saved_addresses')
        .update({ label, address, zone_id: zone_id ?? null, place_type: place_type ?? null })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-addresses'] });
    },
  });
}

export function useSetDefaultAddress() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (addressId: string) => {
      if (!user) throw new Error('Not authenticated');

      // First, unset all defaults for this user
      const { error: unsetError } = await supabase
        .from('saved_addresses')
        .update({ is_default: false })
        .eq('user_id', user.id);

      if (unsetError) throw unsetError;

      // Then set the new default
      const { error: setError } = await supabase
        .from('saved_addresses')
        .update({ is_default: true })
        .eq('id', addressId);

      if (setError) throw setError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-addresses'] });
    },
  });
}

export function useDeleteAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('saved_addresses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-addresses'] });
    },
  });
}
